import { useState } from 'react'
import { Link } from 'react-router-dom'

import type { Alert, Severity } from '@/api/types'
import { SEVERITIES } from '@/api/types'
import {
  Badge,
  Card,
  EmptyState,
  formatRelativeTime,
  LoadingBlock,
  SegmentedControl,
  SeverityBadge,
  useNow,
} from '@/design'
import { usePermissions } from '@/lib/access'
import { ApiError } from '@/lib/api'
import { usePageFreshness } from '@/lib/freshness'

import { AlertFacts } from '@/components/insights/AlertFacts'
import { CATEGORY_LABELS } from '@/lib/insights/categoryLabels'
import { useInsightsRead } from '@/lib/insights/queries'

import { useDrillDown, useInsightAction } from './hooks'
import './insights.css'

const SEVERITY_OPTIONS: ReadonlyArray<{ value: Severity | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  ...SEVERITIES.map((severity) => ({ value: severity, label: severity[0]?.toUpperCase() + severity.slice(1) })),
]

/** What went wrong acting on an alert, in the reader's words. */
function actionErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409) return 'This alert has ended since the page loaded, so there is nothing left to act on.'
    if (error.status === 404) return 'This alert is no longer available.'
    if (error.status === 403) return 'Your role cannot acknowledge or dismiss alerts.'
  }
  return 'The alert could not be updated. Try again.'
}

/**
 * One alert's card. Its own acknowledge/dismiss mutations, so processing one
 * alert never disables another's buttons; both are offered only to a role
 * holding `insights.manage` (the API refuses everyone else).
 */
function AlertCard({ alert, canManage }: { alert: Alert; canManage: boolean }) {
  const href = useDrillDown()
  const now = useNow()
  const acknowledge = useInsightAction((client, id) => client.acknowledgeAlert(id))
  const dismiss = useInsightAction((client, id) => client.dismissAlert(id))
  const failed = acknowledge.error ?? dismiss.error

  return (
    <Card className="in-alert-card">
      <div className="in-alert__header">
        <SeverityBadge severity={alert.severity} />
        <span className="in-muted">{CATEGORY_LABELS[alert.category]}</span>
        {alert.state === 'acknowledged' ? <Badge tone="neutral">Acknowledged</Badge> : null}
        <span className="in-muted in-alert__time">{formatRelativeTime(alert.occurredAt, now)}</span>
      </div>
      <Link className="in-alert__title" to={href(alert.drillDown)}>
        {alert.title}
      </Link>
      <p className="in-alert__subject">{alert.subject}</p>
      <p className="in-alert__explanation">{alert.explanation}</p>
      <AlertFacts facts={alert.facts} />
      {canManage ? (
        <div className="in-action-row">
          <button
            type="button"
            className="ci-button"
            disabled={alert.state === 'acknowledged' || acknowledge.isPending}
            onClick={() => acknowledge.mutate(alert.id)}
          >
            {alert.state === 'acknowledged' ? 'Acknowledged' : 'Acknowledge'}
          </button>
          <button
            type="button"
            className="ci-button"
            data-variant="ghost"
            disabled={dismiss.isPending}
            onClick={() => dismiss.mutate(alert.id)}
          >
            Dismiss
          </button>
        </div>
      ) : null}
      {failed ? (
        <p className="in-alert__error" role="alert">
          {actionErrorMessage(failed)}
        </p>
      ) : null}
    </Card>
  )
}

/**
 * Insights > Alerts: every open alert, most severe first, each with an
 * explanation, the affected entity and the measured figures it was raised on,
 * linking to the page that explains it (suggested next steps arrive with
 * Recommendations). Acknowledging or dismissing is saved for everyone in the
 * tenant; a dismissed alert drops off the list.
 */
export function AlertsPage() {
  const [severity, setSeverity] = useState<Severity | 'all'>('all')
  const alerts = useInsightsRead('alerts', (client) => client.activeAlerts())
  const canManage = usePermissions().can('insights.manage')
  // A real page among not-live ones: it states its own provenance rather than the shell's fallback.
  usePageFreshness(alerts.data?.meta)

  const all = alerts.data?.data ?? []
  const items = severity === 'all' ? all : all.filter((alert) => alert.severity === severity)

  return (
    <div className="in-stack">
      <div className="in-period-row">
        <SegmentedControl label="Filter by severity" options={SEVERITY_OPTIONS} value={severity} onChange={setSeverity} />
        <p className="in-muted">Open alerts on your production websites, as they stand now: the date range does not apply.</p>
      </div>

      {alerts.isPending ? <LoadingBlock rows={6} label="Loading alerts" /> : null}
      {alerts.isError ? (
        <Card>
          <EmptyState variant="outage" description="Alerts could not be loaded." />
        </Card>
      ) : null}
      {alerts.data && all.length === 0 ? (
        <Card>
          <EmptyState variant="no-results" title="No active alerts" description="Nothing needs attention right now." />
        </Card>
      ) : null}
      {alerts.data && all.length > 0 && items.length === 0 ? (
        <Card>
          <EmptyState variant="no-results" compact title="No alerts at this severity" description="Try a different severity, or choose All." />
        </Card>
      ) : null}
      {alerts.data?.meta.truncated ? (
        <p className="in-muted" role="note">
          Showing the {all.length} most severe open alerts; more are open than one page lists.
        </p>
      ) : null}

      {items.length > 0 ? (
        <ul className="in-alert-list" aria-label="Active alerts">
          {items.map((alert) => (
            <li key={alert.id}>
              <AlertCard alert={alert} canManage={canManage} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
